import { gsap } from "gsap";
import { Container, Graphics, IDestroyOptions, Sprite, Text, TextStyle } from "pixi.js";
import { GameFont } from "../../../common/GameFont";
import { UpgradeResourcePackage } from "../resources/UpgradeResourcePackage";
import { UpgradeResourceIcon } from "./UpgradeResourceIcon";
import { UpgradeItemElementType } from "../../../common/models/Building/types/UpgradeItemElementType";
import { StringFormatExtention } from "by/gamefactory/extensions/StringFormatExtention";
import { IUpgradeBuildingModel } from "../../../common/models/Building/upgrade/IUpgradeBuildingModel";
type Timeline = gsap.core.Timeline;

export class UpgradeResourceIndicator extends Container {
    private readonly upgradeResources: UpgradeResourcePackage;
    private readonly animationTimeline: Timeline;

    private icon: UpgradeResourceIcon;
    private bar: Sprite;
    private upgradedPart: Sprite;
    private upgradedMask: Graphics;
    private fixPart: Sprite;
    private fixMask: Graphics;
    private currentResourceCountText: Text;
    private fixResourceCountText: Text;
    private currentResourceCount: number;
    private resourcesLeftCountText: Text;
    private fixResourcesCount: number;
    private experienceText: Text;
    private resourceMaxCount: number;

    public constructor(
        upgradeResources: UpgradeResourcePackage,
        upgradeItemModel: IUpgradeBuildingModel
    ) {
        super();
        this.upgradeResources = upgradeResources;
        this.animationTimeline = new gsap.core.Timeline({ paused: true });
        const elementsData = upgradeItemModel.elements.value;
        this.currentResourceCount = elementsData.upgraded;
        this.resourceMaxCount = elementsData.upgraded + elementsData.needToUpgrade + elementsData.needToFix;
        this.fixResourcesCount = elementsData.needToFix;
        this.build(upgradeItemModel.upgradePoints.value);
        this.updateResource(upgradeItemModel);
    }

    private build(experienceCount: number): void {
        this.icon = new UpgradeResourceIcon(this.upgradeResources, UpgradeItemElementType.APPLE);
        this.addChild(this.icon);
        this.bar = new Sprite(this.upgradeResources.assets_views_upgrade_upgrade_panel_resource_bar_png);
        this.bar.position.set(12.22, 199.22);
        this.addChild(this.bar);
        this.fixMask = new Graphics();
        this.fixMask.beginFill(0xFFFFFF);
        this.fixMask.drawRect(0, 0, this.bar.width, this.bar.height);
        this.fixMask.endFill();
        this.fixMask.position.x = this.bar.position.x - this.fixMask.width;
        this.fixMask.position.y = this.bar.position.y;
        this.addChild(this.fixMask);
        this.fixPart = new Sprite(this.upgradeResources.assets_views_upgrade_upgrade_panel_resource_bar_fix_png);
        this.fixPart.position = this.bar.position;
        this.fixPart.mask = this.fixMask;
        this.addChild(this.fixPart);
        this.upgradedMask = new Graphics();
        this.upgradedMask.beginFill(0xFFFFFF);
        this.upgradedMask.drawRect(0, 0, this.bar.width, this.bar.height);
        this.upgradedMask.endFill();
        this.upgradedMask.position = this.bar.position;
        this.addChild(this.upgradedMask);
        this.upgradedPart = new Sprite(this.upgradeResources.assets_views_upgrade_upgrade_panel_resource_bar_green_png);
        this.upgradedPart.position = this.bar.position;
        this.upgradedPart.mask = this.upgradedMask;
        this.addChild(this.upgradedPart);
        this.currentResourceCountText = new Text(`${StringFormatExtention.formatWithComma(this.currentResourceCount)}`, new LevelTextStyle());
        this.addChild(this.currentResourceCountText);
        this.currentResourceCountText.position.set(25, 200);
        this.fixResourceCountText = new Text(`${StringFormatExtention.formatWithComma(this.fixResourcesCount)}`, new LevelTextStyle());
        this.addChild(this.fixResourceCountText);
        this.fixResourceCountText.position.set(this.bar.position.x, 200);
        this.resourcesLeftCountText = new Text(`${StringFormatExtention.formatWithComma(this.resourceMaxCount)}`, new LevelTextStyle());
        this.addChild(this.resourcesLeftCountText);
        this.resourcesLeftCountText.position.set(this.bar.width - this.resourcesLeftCountText.width, 200);
        this.experienceText = new Text(`GET ${StringFormatExtention.formatWithComma(experienceCount)} XP`, new ExperienceTextStyle());
        this.experienceText.position.set(this.bar.x + this.bar.width * 0.5 - this.experienceText.width * 0.5, this.bar.y + this.bar.height + 5);
        this.addChild(this.experienceText);
    }

    public updateResource(upgradeItemModel: IUpgradeBuildingModel, fromUpgradeOrFix: boolean = false): void {
        this.icon.updateIconByResourceType(upgradeItemModel.elementId.value);
        const elementsData = upgradeItemModel.elements.value;
        this.currentResourceCount = elementsData.upgraded;
        this.resourceMaxCount = elementsData.upgraded + elementsData.needToUpgrade + elementsData.needToFix;
        this.fixResourcesCount = elementsData.needToFix;

        this.experienceText.text = `GET ${StringFormatExtention.formatWithComma(upgradeItemModel.upgradePoints.value)} XP`;
        this.updateProgress();
    }

    public async updateProgress(): Promise<void> {
        this.currentResourceCountText.text = this.currentResourceCount === 0 && this.fixResourcesCount > 0 ? "" : this.currentResourceCount.toString();
        this.fixResourceCountText.text = this.fixResourcesCount === 0 ? "" : this.fixResourcesCount.toString();
        this.resourcesLeftCountText.text = this.fixResourcesCount > 0 && this.currentResourceCount + this.fixResourcesCount === this.resourceMaxCount ? "" : this.resourceMaxCount.toString();

        const minUpgradedResourceCountWidth = this.currentResourceCountText.text === "" || this.currentResourceCount === 0 ? 0 : this.currentResourceCountText.width + 20;
        const minFixResourceCountWidth = this.fixResourceCountText.text === "" ? 0 : this.fixResourceCountText.width + 20;
        const minResourceLeftWidth = this.resourcesLeftCountText.text === "" || this.fixResourcesCount === 0 ? 0 : this.resourcesLeftCountText.width + 20;

        let fixResourcesMaskWidth = Math.max(this.bar.width * this.fixResourcesCount / this.resourceMaxCount, minFixResourceCountWidth);
        let upgradedResourcesMaskWidth = Math.max(this.bar.width * this.currentResourceCount / this.resourceMaxCount, minUpgradedResourceCountWidth);

        const totalWidth = upgradedResourcesMaskWidth + fixResourcesMaskWidth + minResourceLeftWidth;

        if (totalWidth > this.bar.width) {
            fixResourcesMaskWidth = Math.max(this.bar.width * this.fixResourcesCount / this.resourceMaxCount - (this.fixResourcesCount > this.currentResourceCount ? totalWidth - this.bar.width : 0), minFixResourceCountWidth);
            upgradedResourcesMaskWidth = Math.max(this.bar.width * this.currentResourceCount / this.resourceMaxCount - (this.currentResourceCount >= this.fixResourcesCount ? totalWidth - this.bar.width : 0), minUpgradedResourceCountWidth);
        }

        this.animationTimeline.clear();
        this.animationTimeline.to(this.upgradedMask, { width: upgradedResourcesMaskWidth });
        this.animationTimeline.to(this.fixMask.position, { x: this.bar.position.x + upgradedResourcesMaskWidth }, "<");
        this.animationTimeline.to(this.fixMask, { width: fixResourcesMaskWidth }, "<");
        this.animationTimeline.to(this.fixResourceCountText.position, { x: this.bar.position.x + upgradedResourcesMaskWidth + (fixResourcesMaskWidth - this.fixResourceCountText.width) * 0.5 }, "<");

        await this.animationTimeline.play();
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.animationTimeline.kill();
        super.destroy(options);
    }
}

class LevelTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            stroke: "#000000",
            strokeThickness: 1,
            fontSize: 28
        }));
    }
}

class ExperienceTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 32
        }));
    }
}
